import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@/interfaces/common.interface';
import { formatDateTime, timeAgo } from '@/utility/helper';
import { AlertTriangle, AlertCircle, Info, X, CheckCheck, BellOff } from 'lucide-react';
import { useRoleNavigate } from '@/hooks/useRoleNavigate';

interface Props {
  alerts: Alert[];
  loading?: boolean;
  viewAllPath: string;
  onClose: () => void;
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
}

export default function NotificationDropdown({ alerts, loading, viewAllPath, onClose, onMarkRead, onMarkAllRead }: Props) {
  const navigate = useRoleNavigate();
  const severityIcons = { critical: AlertTriangle, warning: AlertCircle, info: Info };
  const severityColors = { critical: 'text-destructive', warning: 'text-warning', info: 'text-info' };

  return (
    <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-card rounded-lg border shadow-xl z-50 animate-in fade-in zoom-in duration-200 origin-top-right">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
        <div className="flex items-center gap-2">
          {/* {alerts.length > 0 && alerts.some(a => !a.read) && (
            <button onClick={onMarkAllRead} className="text-[10px] font-bold text-primary hover:underline flex items-center gap-1 uppercase tracking-wider cursor-pointer">
              <CheckCheck className="w-3 h-3" /> Mark all read
            </button>
          )} */}
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-secondary">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="max-h-80 overflow-y-auto scrollbar-thin">
        {loading ? (
          <div className="p-8 flex flex-col items-center justify-center text-center space-y-2">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-xs text-muted-foreground">Loading notifications...</p>
          </div>
        ) : alerts.length === 0 ? (
          <div className="p-8 flex flex-col items-center justify-center text-center space-y-2">
            <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center">
              <BellOff className="w-5 h-5 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground">All caught up!</p>
            <p className="text-xs text-muted-foreground">No new notifications to show.</p>
          </div>
        ) : (
          alerts
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
            .map(alert => {
              const Icon = severityIcons[alert.severity] || Info;
              return (
                <button
                  key={alert.id}
                  onClick={() => {
                    if (!alert.read) onMarkRead(alert.id);
                    console.log(alert.actionUrl, 'alert.actionUrl')
                    if (alert.actionUrl) navigate(alert.actionUrl);
                    onClose();
                  }}
                  className={`w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-secondary/50 transition-colors ${!alert.read ? 'bg-primary/5' : ''} cursor-pointer`}
                >
                  <div className="flex gap-3">
                    <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${severityColors[alert.severity] || 'text-info'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${!alert.read ? 'font-semibold' : 'font-medium'} text-foreground truncate`}>
                          {alert.title}
                        </p>
                        {!alert.read && <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-1.5" />}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {alert.message}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-1 font-medium">
                        {timeAgo(alert.createdAt.toString())}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })
        )}
      </div>

      {alerts.length > 0 && (
        <div className="px-4 py-2 border-t text-center">
          <button
            type='button'
            onClick={() => { navigate(viewAllPath); onClose(); }}
            className="text-xs font-semibold text-primary hover:underline hover:text-primary/80 transition-colors cursor-pointer"
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
}

